import React from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal, Loader2 } from 'lucide-react';
import Tooltip, { type TooltipPosition } from './Tooltip';
import {
  DROPDOWN_MENU_PAPER,
  DROPDOWN_MENU_ITEM,
  DROPDOWN_MENU_ITEM_DISABLED,
  DROPDOWN_MENU_ITEM_SELECTED,
  DROPDOWN_MENU_ANIMATION,
  DROPDOWN_MENU_KEYFRAMES,
} from './dropdownMenuStyles';

export interface ContextMenuItem {
  label: string;
  onClick?: (e?: React.MouseEvent) => void;
  href?: string; // Standard SaaS: Support direct links for better mobile compatibility
  target?: string; // e.g., '_blank' for new tab
  icon?: React.ElementType;
  disabled?: boolean;
  badgeCount?: number;
  children?: ContextMenuItem[];
}

interface ContextMenuProps {
  items: ContextMenuItem[];
  trigger?: 'more';
  size?: 'sm' | 'md';
  tooltip?: string;
  /** Defaults to Tooltip default (`bottom`). Override only when layout requires it. */
  tooltipPosition?: TooltipPosition;
  triggerLabel?: string;
  triggerIcon?: React.ElementType;
  triggerIconClassName?: string;
  disabled?: boolean;
  onEmptyClick?: (e?: React.MouseEvent) => void;
  /** When true, open the menu (e.g. guided “create project” flow). */
  forceOpen?: boolean;
  /** Notified when open state changes (user close or forceOpen). */
  onOpenChange?: (open: boolean) => void;
  /** Extra classes on the trigger button (e.g. highlight ring). */
  triggerClassName?: string;
  /** Right-click on this element opens the same menu (row, card, etc.). */
  contextTargetRef?: React.RefObject<HTMLElement | null>;
}

const MENU_MARGIN = 8;

type MenuAnchor =
  | { type: 'trigger' }
  | { type: 'point'; top: number; left: number };

function clampMenuPosition(
  anchor: MenuAnchor,
  trigger: HTMLElement | null,
  menu: HTMLElement
): { top: number; left: number } | null {
  const menuWidth = menu.offsetWidth;
  const menuHeight = menu.offsetHeight;
  if (menuWidth <= 0 || menuHeight <= 0) return null;

  if (anchor.type === 'point') {
    let left = anchor.left;
    let top = anchor.top;

    if (left + menuWidth + MENU_MARGIN > window.innerWidth) {
      left = anchor.left - menuWidth;
    }
    if (top + menuHeight + MENU_MARGIN > window.innerHeight) {
      top = anchor.top - menuHeight;
    }

    left = Math.max(MENU_MARGIN, Math.min(left, window.innerWidth - menuWidth - MENU_MARGIN));
    top = Math.max(MENU_MARGIN, Math.min(top, window.innerHeight - menuHeight - MENU_MARGIN));
    return { top, left };
  }

  if (!trigger) return null;

  const triggerRect = trigger.getBoundingClientRect();
  const spaceRight = window.innerWidth - triggerRect.right - MENU_MARGIN;
  const spaceLeft = triggerRect.left - MENU_MARGIN;
  const fitsRight = menuWidth <= spaceRight;
  const fitsLeft = menuWidth <= spaceLeft;

  let left: number;
  if (fitsLeft && !fitsRight) {
    left = triggerRect.left - menuWidth;
  } else if (fitsRight && !fitsLeft) {
    left = triggerRect.right;
  } else if (fitsLeft && fitsRight) {
    left = triggerRect.left > window.innerWidth * 0.55
      ? triggerRect.left - menuWidth
      : triggerRect.right;
  } else {
    left = spaceLeft >= spaceRight
      ? MENU_MARGIN
      : window.innerWidth - menuWidth - MENU_MARGIN;
  }

  left = Math.max(MENU_MARGIN, Math.min(left, window.innerWidth - menuWidth - MENU_MARGIN));

  let top = triggerRect.top;
  if (top + menuHeight + MENU_MARGIN > window.innerHeight) {
    top = window.innerHeight - menuHeight - MENU_MARGIN;
  }
  if (top < MENU_MARGIN) {
    top = MENU_MARGIN;
  }

  return { top, left };
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  items,
  trigger = 'more',
  size = 'sm',
  tooltip,
  tooltipPosition,
  triggerLabel,
  triggerIcon,
  triggerIconClassName = '',
  disabled = false,
  onEmptyClick,
  forceOpen = false,
  onOpenChange,
  triggerClassName = '',
  contextTargetRef,
}) => {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const submenuRef = React.useRef<HTMLDivElement>(null);
  const nestedSubmenuRef = React.useRef<HTMLDivElement>(null);
  const itemRefs = React.useRef<Map<number, HTMLButtonElement | HTMLAnchorElement>>(new Map());
  const nestedItemRefs = React.useRef<Map<number, HTMLButtonElement | HTMLAnchorElement>>(new Map());
  const [menuAnchor, setMenuAnchor] = React.useState<MenuAnchor>({ type: 'trigger' });
  const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number } | null>(null);
  const [menuReady, setMenuReady] = React.useState(false);
  const [submenuPosition, setSubmenuPosition] = React.useState<{ top: number; left: number } | null>(null);
  const [nestedSubmenuPosition, setNestedSubmenuPosition] = React.useState<{ top: number; left: number } | null>(null);
  const [openSubIdx, setOpenSubIdx] = React.useState<number | null>(null);
  const [openNestedSubIdx, setOpenNestedSubIdx] = React.useState<number | null>(null);

  const hasItems = items.length > 0;

  const setOpenState = React.useCallback((next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  }, [onOpenChange]);

  const closeAllMenus = React.useCallback(() => {
    setOpenState(false);
    setOpenSubIdx(null);
    setOpenNestedSubIdx(null);
  }, [setOpenState]);

  const openMenuAtPoint = React.useCallback((clientX: number, clientY: number) => {
    if (!hasItems || disabled) return;
    setMenuAnchor({ type: 'point', top: clientY, left: clientX });
    setOpenState(true);
    setOpenSubIdx(null);
    setOpenNestedSubIdx(null);
  }, [disabled, hasItems, setOpenState]);

  React.useEffect(() => {
    if (forceOpen && hasItems && !open) {
      setMenuAnchor({ type: 'trigger' });
      setOpenState(true);
    }
  }, [forceOpen, hasItems, open, setOpenState]);

  React.useEffect(() => {
    if (!open) {
      setMenuPosition(null);
      setMenuReady(false);
      setOpenSubIdx(null);
      setOpenNestedSubIdx(null);
      setSubmenuPosition(null);
      setNestedSubmenuPosition(null);
      setMenuAnchor({ type: 'trigger' });
    }
  }, [open]);

  React.useEffect(() => {
    setOpenNestedSubIdx(null);
    setNestedSubmenuPosition(null);
  }, [openSubIdx]);

  // Right-click on parent row/card opens the same menu
  React.useEffect(() => {
    const target = contextTargetRef?.current;
    if (!target || disabled || !hasItems) return;

    const handleContextMenu = (e: MouseEvent) => {
      const interactive = (e.target as Element | null)?.closest(
        'input, textarea, select, button, a, [data-skip-context-menu]'
      );
      if (interactive) return;

      e.preventDefault();
      e.stopPropagation();
      openMenuAtPoint(e.clientX, e.clientY);
    };

    target.addEventListener('contextmenu', handleContextMenu);
    return () => target.removeEventListener('contextmenu', handleContextMenu);
  }, [contextTargetRef, disabled, hasItems, openMenuAtPoint]);

  // Close menu on outside click or Escape
  React.useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target) ||
        submenuRef.current?.contains(target) ||
        nestedSubmenuRef.current?.contains(target)
      ) {
        return;
      }
      closeAllMenus();
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (openNestedSubIdx !== null) {
          setOpenNestedSubIdx(null);
          return;
        }
        if (openSubIdx !== null) {
          setOpenSubIdx(null);
          return;
        }
        closeAllMenus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, closeAllMenus, openNestedSubIdx, openSubIdx]);

  // Calculate menu position after mount (trigger or cursor)
  React.useEffect(() => {
    if (!open || !hasItems) return;

    const updatePosition = () => {
      const menu = menuRef.current;
      if (!menu) return;

      const next = clampMenuPosition(menuAnchor, triggerRef.current, menu);
      if (!next) return;

      setMenuPosition(next);
      setMenuReady(true);
    };

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      updatePosition();
      raf2 = requestAnimationFrame(updatePosition);
    });

    const handleScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      updatePosition();
    };

    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('resize', updatePosition, { passive: true });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, hasItems, items.length, menuAnchor]);

  // Calculate submenu position
  React.useEffect(() => {
    if (openSubIdx === null || !menuRef.current) {
      setSubmenuPosition(null);
      return;
    }

    const itemButton = itemRefs.current.get(openSubIdx);
    const menu = menuRef.current;
    if (!itemButton || !menu) return;

    const updateSubmenuPosition = () => {
      const itemRect = itemButton.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const submenu = submenuRef.current;
      const submenuWidth = submenu?.offsetWidth || 180;
      const submenuHeight = submenu?.offsetHeight || 180;
      const gap = 4;

      let left = menuRect.right + gap;
      if (left + submenuWidth + MENU_MARGIN > window.innerWidth) {
        left = menuRect.left - submenuWidth - gap;
        if (left < MENU_MARGIN) {
          left = window.innerWidth - submenuWidth - MENU_MARGIN;
        }
      }

      let top = itemRect.top;
      if (top + submenuHeight + MENU_MARGIN > window.innerHeight) {
        top = window.innerHeight - submenuHeight - MENU_MARGIN;
      }
      if (top < MENU_MARGIN) {
        top = MENU_MARGIN;
      }

      setSubmenuPosition({ top, left });
    };

    updateSubmenuPosition();

    const rafId = requestAnimationFrame(updateSubmenuPosition);

    window.addEventListener('scroll', updateSubmenuPosition, { passive: true, capture: true });
    window.addEventListener('resize', updateSubmenuPosition, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', updateSubmenuPosition, { capture: true });
      window.removeEventListener('resize', updateSubmenuPosition);
    };
  }, [openSubIdx, open]);

  // Nested submenu (children of a submenu item)
  React.useEffect(() => {
    if (openNestedSubIdx === null || !submenuRef.current) {
      setNestedSubmenuPosition(null);
      return;
    }

    const itemButton = nestedItemRefs.current.get(openNestedSubIdx);
    const parentMenu = submenuRef.current;
    if (!itemButton || !parentMenu) return;

    const updateNestedPosition = () => {
      const itemRect = itemButton.getBoundingClientRect();
      const parentRect = parentMenu.getBoundingClientRect();
      const nested = nestedSubmenuRef.current;
      const nestedWidth = nested?.offsetWidth || 180;
      const nestedHeight = nested?.offsetHeight || 180;
      const gap = 4;

      let left = parentRect.right + gap;
      if (left + nestedWidth + MENU_MARGIN > window.innerWidth) {
        left = parentRect.left - nestedWidth - gap;
        if (left < MENU_MARGIN) {
          left = window.innerWidth - nestedWidth - MENU_MARGIN;
        }
      }

      let top = itemRect.top;
      if (top + nestedHeight + MENU_MARGIN > window.innerHeight) {
        top = window.innerHeight - nestedHeight - MENU_MARGIN;
      }
      if (top < MENU_MARGIN) {
        top = MENU_MARGIN;
      }

      setNestedSubmenuPosition({ top, left });
    };

    updateNestedPosition();
    const rafId = requestAnimationFrame(updateNestedPosition);
    window.addEventListener('scroll', updateNestedPosition, { passive: true, capture: true });
    window.addEventListener('resize', updateNestedPosition, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', updateNestedPosition, { capture: true });
      window.removeEventListener('resize', updateNestedPosition);
    };
  }, [openNestedSubIdx, openSubIdx, open]);

  const Icon = triggerIcon || MoreHorizontal;
  const sizeClasses = size === 'sm' ? 'p-1.5' : 'p-2';
  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasItems && onEmptyClick) {
      onEmptyClick(e);
      return;
    }
    if (hasItems) {
      setMenuAnchor({ type: 'trigger' });
      setOpenState(!open);
      setOpenSubIdx(null);
      setOpenNestedSubIdx(null);
    }
  };

  const handleTriggerContextMenu = (e: React.MouseEvent) => {
    if (!hasItems || disabled) return;
    e.preventDefault();
    e.stopPropagation();
    openMenuAtPoint(e.clientX, e.clientY);
  };

  const isDisabled = disabled || !hasItems;

  const triggerButton = (
    <button
      type="button"
      ref={triggerRef}
      onClick={handleTriggerClick}
      onContextMenu={handleTriggerContextMenu}
      disabled={isDisabled}
      className={
        triggerLabel
          ? `inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
              isDisabled
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-primary-600 hover:text-primary-700'
            } ${triggerClassName}`
          : `${sizeClasses} rounded-lg transition-all duration-200 ${
              isDisabled
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-primary-600 hover:text-primary-700 hover:bg-primary-50 active:bg-primary-100 hover:scale-105 active:scale-95'
            } ${triggerClassName}`
      }
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={triggerLabel || 'Actions'}
    >
      <Icon className={`${triggerLabel ? 'w-4 h-4' : iconSize} ${triggerIconClassName}`} />
      {triggerLabel && <span>{triggerLabel}</span>}
    </button>
  );

  return (
    <>
      <style>{DROPDOWN_MENU_KEYFRAMES}</style>
      {tooltip ? (
        <Tooltip content={tooltip} position={tooltipPosition}>
          {triggerButton}
        </Tooltip>
      ) : (
        triggerButton
      )}

      {open && hasItems && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className={`fixed z-[2000] ${DROPDOWN_MENU_PAPER}`}
          style={{
            top: menuPosition?.top ?? 0,
            left: menuPosition?.left ?? 0,
            visibility: menuReady ? 'visible' : 'hidden',
            pointerEvents: menuReady ? 'auto' : 'none',
            animation: menuReady ? DROPDOWN_MENU_ANIMATION : 'none',
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          {items.map((item, idx) => {
            const ItemIcon = item.icon;
            const hasChildren = !!item.children?.length;
            const isSubmenuOpen = openSubIdx === idx;

            return (
              <div key={idx} className="relative">
                {item.href ? (
                  <a
                    ref={(el) => {
                      if (el) itemRefs.current.set(idx, el);
                      else itemRefs.current.delete(idx);
                    }}
                    role="menuitem"
                    href={item.href}
                    target={item.target || '_self'}
                    rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (hasChildren) {
                        e.preventDefault();
                        setOpenSubIdx(isSubmenuOpen ? null : idx);
                        return;
                      }
                      setOpenState(false);
                      setOpenSubIdx(null);
                      item.onClick?.(e);
                    }}
                    className={`group w-full ${DROPDOWN_MENU_ITEM} ${
                      item.disabled ? DROPDOWN_MENU_ITEM_DISABLED : ''
                    } ${isSubmenuOpen ? DROPDOWN_MENU_ITEM_SELECTED : ''}`}
                  >
                    {ItemIcon && (
                      <ItemIcon className={`w-4 h-4 text-primary-600 group-hover:text-primary-700 flex-shrink-0 transition-colors ${
                        ItemIcon === Loader2 ? 'animate-spin' : ''
                      }`} />
                    )}
                    <span className="flex-1 min-w-0 truncate max-w-[12rem]">{item.label}</span>
                    {typeof item.badgeCount === 'number' && (
                      <span className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold rounded-md bg-primary-100 text-gray-700">
                        {item.badgeCount}
                      </span>
                    )}
                    {hasChildren && (
                      <svg className="w-3.5 h-3.5 text-gray-400 ml-auto flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </a>
                ) : (
                  <button
                    ref={(el) => {
                      if (el) itemRefs.current.set(idx, el);
                      else itemRefs.current.delete(idx);
                    }}
                    role="menuitem"
                    disabled={item.disabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (hasChildren) {
                        setOpenSubIdx(isSubmenuOpen ? null : idx);
                        return;
                      }
                      setOpenState(false);
                      setOpenSubIdx(null);
                      item.onClick?.(e);
                    }}
                    className={`group w-full ${DROPDOWN_MENU_ITEM} ${
                      item.disabled ? DROPDOWN_MENU_ITEM_DISABLED : ''
                    } ${isSubmenuOpen ? DROPDOWN_MENU_ITEM_SELECTED : ''}`}
                  >
                    {ItemIcon && (
                      <ItemIcon className={`w-4 h-4 text-primary-600 group-hover:text-primary-700 flex-shrink-0 transition-colors ${
                        ItemIcon === Loader2 ? 'animate-spin' : ''
                      }`} />
                    )}
                    <span className="flex-1 min-w-0 truncate max-w-[12rem]">{item.label}</span>
                    {typeof item.badgeCount === 'number' && (
                      <span className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold rounded-md bg-primary-100 text-gray-700">
                        {item.badgeCount}
                      </span>
                    )}
                    {hasChildren && (
                      <svg className="w-3.5 h-3.5 text-gray-400 ml-auto flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>,
        document.body
      )}

      {open && openSubIdx !== null && items[openSubIdx]?.children && submenuPosition && createPortal(
        <div
          ref={submenuRef}
          role="menu"
          className={`fixed z-[2001] ${DROPDOWN_MENU_PAPER}`}
          style={{
            top: submenuPosition.top,
            left: submenuPosition.left,
            animation: 'contextSubmenuSlideIn 150ms ease-out',
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          {items[openSubIdx].children!.map((childItem, childIdx) => {
            const ChildIcon = childItem.icon;
            const hasNested = !!childItem.children?.length;
            const isNestedOpen = openNestedSubIdx === childIdx;
            const itemClass = `group w-full ${DROPDOWN_MENU_ITEM} ${
              childItem.disabled ? DROPDOWN_MENU_ITEM_DISABLED : ''
            } ${isNestedOpen ? DROPDOWN_MENU_ITEM_SELECTED : ''}`;
            return childItem.href && !hasNested ? (
              <a
                key={childIdx}
                ref={(el) => {
                  if (el) nestedItemRefs.current.set(childIdx, el);
                  else nestedItemRefs.current.delete(childIdx);
                }}
                role="menuitem"
                href={childItem.href}
                target={childItem.target || '_self'}
                rel={childItem.target === '_blank' ? 'noopener noreferrer' : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  closeAllMenus();
                  childItem.onClick?.(e);
                }}
                className={itemClass}
              >
                {ChildIcon && (
                  <ChildIcon className={`w-4 h-4 text-primary-600 group-hover:text-primary-700 flex-shrink-0 transition-colors ${
                    ChildIcon === Loader2 ? 'animate-spin' : ''
                  }`} />
                )}
                <span className="flex-1 min-w-0 truncate max-w-[12rem]">{childItem.label}</span>
              </a>
            ) : (
              <button
                key={childIdx}
                ref={(el) => {
                  if (el) nestedItemRefs.current.set(childIdx, el);
                  else nestedItemRefs.current.delete(childIdx);
                }}
                role="menuitem"
                disabled={childItem.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasNested) {
                    setOpenNestedSubIdx(isNestedOpen ? null : childIdx);
                    return;
                  }
                  closeAllMenus();
                  childItem.onClick?.(e);
                }}
                className={itemClass}
              >
                {ChildIcon && (
                  <ChildIcon className={`w-4 h-4 text-primary-600 group-hover:text-primary-700 flex-shrink-0 transition-colors ${
                    ChildIcon === Loader2 ? 'animate-spin' : ''
                  }`} />
                )}
                <span className="flex-1 min-w-0 truncate max-w-[12rem]">{childItem.label}</span>
                {hasNested && (
                  <svg className="w-3.5 h-3.5 text-gray-400 ml-auto flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>,
        document.body
      )}

      {open &&
        openSubIdx !== null &&
        openNestedSubIdx !== null &&
        items[openSubIdx]?.children?.[openNestedSubIdx]?.children &&
        nestedSubmenuPosition &&
        createPortal(
          <div
            ref={nestedSubmenuRef}
            role="menu"
            className={`fixed z-[2002] ${DROPDOWN_MENU_PAPER}`}
            style={{
              top: nestedSubmenuPosition.top,
              left: nestedSubmenuPosition.left,
              animation: 'contextSubmenuSlideIn 150ms ease-out',
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
          >
            {items[openSubIdx].children![openNestedSubIdx].children!.map((nestedItem, nestedIdx) => {
              const NestedIcon = nestedItem.icon;
              const itemClass = `group w-full ${DROPDOWN_MENU_ITEM} ${
                nestedItem.disabled ? DROPDOWN_MENU_ITEM_DISABLED : ''
              }`;
              return (
                <button
                  key={nestedIdx}
                  role="menuitem"
                  disabled={nestedItem.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeAllMenus();
                    nestedItem.onClick?.(e);
                  }}
                  className={itemClass}
                >
                  {NestedIcon && (
                    <NestedIcon
                      className={`w-4 h-4 text-primary-600 group-hover:text-primary-700 flex-shrink-0 transition-colors ${
                        NestedIcon === Loader2 ? 'animate-spin' : ''
                      }`}
                    />
                  )}
                  <span className="flex-1 min-w-0 truncate max-w-[12rem]">{nestedItem.label}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

export default ContextMenu;

/** Provides a shared ref for row/card + ContextMenu right-click target. */
export function ContextMenuTarget<T extends HTMLElement = HTMLElement>({
  children,
}: {
  children: (targetRef: React.RefObject<T>) => React.ReactNode;
}) {
  const targetRef = React.useRef<T>(null) as React.RefObject<T>;
  return <>{children(targetRef)}</>;
}
